/// <amd-module name="Wit/WorkItemForm/Details/XMLControlUtils" />
import * as Resources from "Wit/WorkItemForm/Resources";
import { format } from "VSS/Core/Util/String";

export interface ILabelLinkParams {
    value: string;
    original: boolean;
}

export interface ILabelLink {
    url: string;
    urlRoot: string;
    urlPath: string;
    params: ILabelLinkParams[];
}

export interface ILabelData {
    content?: string;
    link?: ILabelLink;
}

export interface IControlXMLData {
    allowScript?: boolean;
    content?: any;
    height?: number;
    labelData: ILabelData[];
    link?: any;
    reloadOnParamChange?: boolean;
}

export function readControlXMLData(metadata: string): IControlXMLData | undefined {
    let controlNode: XMLDocument;
    try {
        const domParser = new window.DOMParser();
        controlNode = domParser.parseFromString(metadata, "text/xml");
    } catch (ex) {
        console.error(format(Resources.FailedToParseLabelXML, metadata));
        return undefined;
    }

    const xmlData: IControlXMLData = { labelData: [] };
    const children = controlNode.childNodes;
    if (children && children.length) {
        children.forEach((childNode: ChildNode) => {
            switch (childNode.nodeName) {
                // There are more options
                case "Link":
                    xmlData.labelData = [{ link: readLinkOptions(childNode) }];
                    break;
                case "LabelText":
                    readLabelTextOptions(childNode, xmlData);
                    break;
                case "WebpageControlOptions":
                    readWebPageControlOptions(childNode, xmlData);
                    break;
            }
        });
    }

    return xmlData;
}

function readLinkOptions(linkNode: ChildNode) {
    const urlRoot = (linkNode as Element).getAttribute("UrlRoot") || "";
    const urlPath = (linkNode as Element).getAttribute("UrlPath") || "";

    const link: ILabelLink = {
        urlRoot: urlRoot,
        urlPath: urlPath,
        url: combineUrl(urlRoot, urlPath),
        params: []
    };

    // Checking to see any link param exists
    if (linkNode.childNodes && linkNode.childNodes.length) {
        linkNode.childNodes.forEach(childNode => {
            var index;
            if (childNode.nodeName === "Param") {
                // Adding params
                index = parseInt((childNode as Element).getAttribute("Index") || "", 10);
                if (!isNaN(index)) {
                    link.params[index] = {
                        value: (childNode as Element).getAttribute("Value") || "",
                        original: (childNode as Element).getAttribute("Type") === "Original"
                    };
                }
            }
        });
    }

    return link;
}

function readTextNode(textNode: ChildNode): ILabelData {
    var textData: ILabelData = {};

    if (textNode.childNodes && textNode.childNodes.length) {
        textNode.childNodes.forEach(childNode => {
            if (childNode.nodeName === "Link") {
                textData.link = readLinkOptions(childNode);
            } else {
                textData.content = childNode.nodeValue ?? undefined;
            }
        });
    }

    return textData;
}

export function readLabelTextOptions(labelTextNode: ChildNode, xmlData: IControlXMLData) {
    var textNodes: ILabelData[] = [];
    if (labelTextNode.childNodes && labelTextNode.childNodes.length) {
        labelTextNode.childNodes.forEach(childNode => {
            textNodes.push(readTextNode(childNode));
        });
    }
    xmlData.labelData = textNodes;
}

export function readWebPageControlOptions(pageNode: ChildNode, xmlData: IControlXMLData) {
    var children = pageNode.childNodes;

    xmlData.allowScript = (pageNode as Element).getAttribute("AllowScript") === "true"; // default false
    xmlData.reloadOnParamChange = (pageNode as Element).getAttribute("ReloadOnParamChange") !== "false"; // default true

    children.forEach(child => {
        switch (child.nodeName) {
            case "Link":
                xmlData.link = readLinkOptions(child);
                break;

            case "Content":
                xmlData.content = child.textContent;
                break;
        }
    });
}

/// <summary>Combinees UrlRoot and UrlPath</summary>
/// <param name="urlRoot" type="string">Contains urlRoot with/without '/' at end</param>
/// <param name="urlPath" type="string">Contains urlPath with/without '/' at beginning</param>
/// <Return type="string">Returns a valid url string containing urlRoot, urlPath</param>
export function combineUrl(urlRoot: string, urlPath: string): string {
    // trim parts
    if (urlRoot) {
        urlRoot = urlRoot.trim();
    }
    if (urlPath) {
        urlPath = urlPath.trim();
    }

    // check if only one part is specified
    if (!urlRoot && !urlPath) {
        return "";
    }
    if (!urlRoot) {
        return urlPath;
    }
    if (!urlPath) {
        return urlRoot;
    }
    // both parts have slash, remove the duplicate
    if (urlRoot.charAt(urlRoot.length - 1) === "/" && urlPath.charAt(0) === "/") {
        return urlRoot + urlPath.substring(1, urlPath.length);
    }
    // one part has a slash
    if (urlRoot.charAt(urlRoot.length - 1) === "/" || urlPath.charAt(0) === "/") {
        return urlRoot + urlPath;
    }
    // none have slash
    return urlRoot + "/" + urlPath;
}
